import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import UploadCSV from './pages/UploadCSV';
import DataManagement from './pages/DataManagement';
import DataFinal from './pages/DataFinal';
import PushedData from './pages/PushedData';
import Users from './pages/Users';
import ActivityLog from './pages/ActivityLog';
import RejectedData from './pages/RejectedData';
import Settings from './pages/Settings';
import MarketplaceData from './pages/MarketplaceData';
import AppLayout from './components/layout/AppLayout';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

type AppRole = 'ADMIN' | 'SUPER_ADMIN' | 'CONTRIBUTOR';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const RoleProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles: AppRole[];
}> = ({ children, allowedRoles }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user || !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const MarketplacePage: React.FC = () => {
  const { source } = useParams();
  if (!source) return <Navigate to="/" replace />;
  return <MarketplaceData key={source} source={source} />;
};

const App: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} 
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route
          path="upload"
          element={
            <RoleProtectedRoute allowedRoles={['SUPER_ADMIN', 'CONTRIBUTOR']}>
              <UploadCSV />
            </RoleProtectedRoute>
          }
        />
        <Route path="data" element={<DataManagement />} />
        <Route path="data-final" element={<DataFinal />} />
        <Route path="pushed-data" element={<PushedData />} />
        <Route path="users" element={<Users />} />
        <Route path="rejected-data" element={<RejectedData />} />
        <Route path="activity-log" element={<ActivityLog />} />
        <Route
          path="settings"
          element={
            <RoleProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Settings />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="marketplace/:source"
          element={
            <RoleProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <MarketplacePage />
            </RoleProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
